import React, { useState } from "react";

import { SaveButton, useNotify, useRedirect } from "react-admin";
import { useForm } from "react-final-form";

import { logError } from "../../../utils/appUtils";
import {
  buildCarrierIdentityFields,
  changedCarrierIdentity,
  normalizeCarrierIdentifiers,
  validateCarrierUnicity,
} from "../../../utils/carrierIdentity";

export const ValidateSaveButton = ({ record, backPath, ...props }) => {
  const form = useForm();
  const notify = useNotify();
  const redirect = useRedirect();
  const [isSaving, setIsSaving] = useState(false);

  const handleValidateSave = async () => {
    const formState = form.getState();

    // Nothing to check on an invalid form — hand it straight back so RA shows
    // the field errors rather than swallowing the click.
    if (formState.invalid) {
      props.handleSubmitWithRedirect();
      return;
    }

    // Only the identity fields this save moves, so a rename cannot be refused
    // over the phone number the record already had. Nothing moved means nothing
    // to look up.
    const changed = changedCarrierIdentity(formState.values, record);
    if (Object.keys(changed).length === 0) {
      props.handleSubmitWithRedirect();
      return;
    }

    setIsSaving(true);

    const duplicate = await validateCarrierUnicity(
      // The numbers ride along whether or not this save moved them: they are
      // what vetoes a name or address collision against a carrier that holds a
      // different number under the same authority, so a rename must be checked
      // in their light rather than on the name alone.
      { ...changed, identifiers: formState.values?.identifiers },
      { excludeId: record?.id ?? formState.values?.id },
    );

    if (duplicate) {
      notify(duplicate.message, "warning");
      setIsSaving(false);
      return;
    }

    props.handleSubmitWithRedirect();
  };

  const transform = (data) => {
    data = { ...data };
    // Optional now: kiosk-created carriers have no address until a manager
    // adds one here.
    data.email = data.email ? data.email.toLowerCase() : undefined;

    // Only include fields that are valid for UpdateCarrierInput
    const validFields = {
      id: data.id,
      active: data.active,
      businessName: data.businessName,
      email: data.email,
      phone: data.phone,
      reloadYardId: data.reloadYardId,
      // Rewritten on every save, or an edited email would leave the record
      // matching its old address in the next create's uniqueness check.
      ...buildCarrierIdentityFields(data),
      identifiers: normalizeCarrierIdentifiers(data.identifiers),
    };

    return validFields;
  };

  const onSuccess = async ({ data }) => {
    try {
      notify(`Carrier company updated successfully.`);
      redirect("/carriers");
    } catch (error) {
      logError(
        "managerApp:carriers:ValidateSaveButton:onSuccess",
        error,
        notify
      );
    } finally {
      setIsSaving(false);
    }
  };

  const onFailure = (error) => {
    setIsSaving(false);
    logError("managerApp:carriers:ValidateSaveButton:onFailure", error, notify);
  };

  return (
    <SaveButton
      {...props}
      submitOnEnter={false}
      undoable={false}
      disabled={props.pristine}
      saving={isSaving}
      handleSubmitWithRedirect={handleValidateSave}
      transform={transform}
      onSuccess={onSuccess}
      onFailure={onFailure}
    />
  );
};
